import type { SmtpSettings } from 'src/types/checkout';

import { z as zod } from 'zod';
import React, { useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Box,
  Switch,
  Tooltip,
  Typography,
  IconButton,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { useChannelContext } from '../../hooks';
import { RenderPrevNextBtn } from '../render-prevnext-btn';

// smtpEnabled            Boolean  @default(false)
// smtpAddress            String   @default("")
// smtpPort               Int      @default(0)
// smtpLogin              String   @default("")
// smtpPassword           String   @default("")
// smtpDomain             String   @default("")

const fldList = [
  { name: 'smtpEnabled', required: false },
  { name: 'smtpAddress', required: true },
  { name: 'smtpPort', required: true },
  { name: 'smtpLogin', required: true },
  { name: 'smtpPassword', required: true },
  { name: 'smtpDomain', required: true },
];

const defaultValues: SmtpSettings = {
  smtpEnabled: true,
  smtpAddress: '',
  smtpPort: 0,
  smtpLogin: '',
  smtpPassword: '',
  smtpDomain: '',
};

export const EmailSMTP = () => {
  const { t } = useTranslate('translation');
  const showPassword = useBoolean();

  const { handleSMTPDetails, state: channelState } = useChannelContext();

  const SmtpSettingSchema = validationSchema(fldList, t).superRefine((data, ctx) => {
    const smtpData = data as SmtpSettings; // Explicitly cast to SmtpSettings

    if (smtpData.smtpEnabled) {
      // Check each field and add an error if it is invalid
      if (!smtpData.smtpAddress.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpAddress'],
          message: `${t('SMTP_ADDRESS')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!smtpData.smtpPort || smtpData.smtpPort <= 0) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpPort'],
          message: t('PORT_POSITIVE_NUMBER'),
        });
      }

      if (!smtpData.smtpLogin.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpLogin'],
          message: `${t('SMTP_LOGIN')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!smtpData.smtpPassword.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpPassword'],
          message: `${t('SMTP_PASSWORD')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!smtpData.smtpDomain.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['smtpDomain'],
          message: `${t('SMTP_DOMAIN')} ${t('IS_REQUIRED')}`,
        });
      }
    }
  });

  const methods = useForm<SmtpSettings>({
    mode: 'all',
    resolver: zodResolver(SmtpSettingSchema),
    defaultValues: channelState.smtpDetails,
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    watch,
  } = methods;

  const watchSmtpEnabled = watch('smtpEnabled');

  const onSubmit = handleSubmit((data) => {
    if (!data.smtpEnabled) {
      const defaultValObj = {
        smtpAddress: '',
        smtpPort: 0,
        smtpLogin: '',
        smtpPassword: '',
        smtpDomain: '',
      };

      handleSMTPDetails({ ...defaultValObj, smtpEnabled: false });
      reset({ ...defaultValObj, smtpEnabled: false });
    } else {
      handleSMTPDetails(data);
    }
  });

  const btnDisableHandler = () => {
    if (watchSmtpEnabled) {
      const {
        smtpAddress: address,
        smtpPort: port,
        smtpLogin: login,
        smtpPassword: password,
        smtpDomain: domain,
      } = watch();

      return !address || !port || !login || !password || !domain;
    }
    return false;
  };

  useEffect(() => {
    if (!watchSmtpEnabled) clearErrors();
  }, [watchSmtpEnabled]);

  const renderFields = () => (
    <Box sx={{ width: '60%', alignSelf: 'end' }}>
      {watchSmtpEnabled && ( // Only show fields if smtpEnabled is true
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Field.Text
            name="smtpAddress"
            label={t('ADDRESS')}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Number
            name="smtpPort"
            label={t('PORT')}
            type="number"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Text
            name="smtpLogin"
            label={t('LOGIN')}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Text
            name="smtpPassword"
            label={t('PASSWORD')}
            type={showPassword.value ? 'text' : 'password'}
            autoComplete="off"
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  autoComplete: 'new-password',
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={showPassword.value ? `${t('HIDE_PASSWORD')}` : `${t('SHOW_PASSWORD')}`}
                    >
                      <IconButton onClick={showPassword.onToggle} edge="end">
                        <Iconify
                          icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
              htmlInput: { autoComplete: 'off' },
            }}
          />

          <Field.Text
            name="smtpDomain"
            label={t('DOMAIN')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
      )}
    </Box>
  );

  const enableProviderConfigTitle = t('ENABLE_PROVIDER_CONFIGURATION').replace(
    '{PROVIDER_NAME}',
    t('SMTP')
  );

  const sendProviderDetailsTitle = t('SEND_PROVIDER_DETAILS').replace('{PROVIDER_NAME}', t('SMTP'));

  return (
    <>
      {/* EmailSMTP */}
      <Form methods={methods} onSubmit={onSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, ml: 8 }}>
              <Typography variant="subtitle1">{t('SMTP')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {sendProviderDetailsTitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '60%' }}>
              <FormControlLabel
                control={
                  <Controller
                    name="smtpEnabled"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        color="primary"
                        checked={field.value} // Bind the checked state to the form value
                        onChange={(e) => field.onChange(e.target.checked)} // Update the form value on change
                      />
                    )}
                  />
                }
                label={<Typography variant="subtitle1">{enableProviderConfigTitle}</Typography>}
              />

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('ENABLE_SMTP_DESCRIPTION')}
              </Typography>
            </Box>
          </Box>
          {renderFields()}
          <Box sx={{ flexGrow: 1 }} />
          <RenderPrevNextBtn isNextDisabled={btnDisableHandler()} />
        </Box>
      </Form>
    </>
  );
};
